<script lang="ts" setup>
import {
  AppPermissionsEnum,
  CalibrationDataOverviewDto,
  CreateOtherQaEquipmentDto,
  CreateQaEquipmentRtSystemDto,
  instanceOfUnicheckDeviceDto,
  instanceOfUnicheckSimulatorDto,
  PtwRegistrationState,
  type QaEquipmentRtSystemDto,
} from '@/api/generator';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { usePermissionsStore } from '@/stores/permissionsStore.ts';
import CalibrationInformation from '@/views/qaEquipment/components/CalibrationInformation.vue';
import QaEquipmentActivateNewCalibrationDialog from '@/views/qaEquipment/components/QaEquipmentActivateNewCalibrationDialog.vue';
import QaEquipmentSupportRequestCalibrationInvalidDialog from '@/views/qaEquipment/components/QaEquipmentSupportRequestCalibrationInvalidDialog.vue';
import { useToggle } from '@vueuse/core';
import { computed } from 'vue';
import { isRegistrablePtwQaEquipment } from '@/views/qaEquipment/utils.ts';

type QaEquipmentCalibrationTabProps = {
  qaEquipment: Partial<
    QaEquipmentRtSystemDto | CreateQaEquipmentRtSystemDto | CreateOtherQaEquipmentDto
  >;
  calibrationData?: CalibrationDataOverviewDto;
};
const props = defineProps<QaEquipmentCalibrationTabProps>();

const isRegistered = computed(
  () =>
    isRegistrablePtwQaEquipment(props.qaEquipment) &&
    props.qaEquipment.registrationState === PtwRegistrationState.Registered,
);

// Permissions
const { hasPermissions } = usePermissionsStore();
const canActivate = computed(() =>
  hasPermissions([AppPermissionsEnum.LocalDosimetryAndQaEquipmentEdit]),
);

// Activate new calibration
const [isActivateCalibrationDialogOpen, toggleActivateCalibrationDialog] = useToggle(false);
// Contact Support
const [isContactSupportDialogOpen, toggleContactSupportDialog] = useToggle(false);
</script>

<template>
  <template v-if="!isRegistered">
    <p class="text-body-1 text-primary-lighten-2 mt-4">
      {{ $t('CalibrationInformationOnlyWhenDeviceRegisteredMsg') }}
    </p>
  </template>
  <template v-else>
    <!-- New Calibration is available -->
    <template v-if="calibrationData?.newDownloadedCalibration">
      <p class="text-body-1 mb-6 text-pre-line">
        {{ $t('ANewCalibrationCertificateIsAvailableMsg') }}
      </p>
      <h4>{{ $t('NewCalibration') }}</h4>
      <CalibrationInformation
        v-if="calibrationData.newDownloadedCalibration"
        class="mb-2"
        :calibrationData="calibrationData.newDownloadedCalibration"
        hideActivationDate
        hideCalibrationState
      />
      <Tooltip :showPointer="true" :disabled="canActivate" forceActive>
        <template #target="{ props: tooltipTargetProps }">
          <div v-bind="tooltipTargetProps" class="mb-6 d-inline-block">
            <PrimaryVBtn
              :disabled="
                !canActivate ||
                calibrationData?.existingCalibrations?.[0].calibrationState === 'Invalid'
              "
              :text="$t('ActivateNow')"
              @click="toggleActivateCalibrationDialog(true)"
            />
          </div>
        </template>
        <template #tooltip>
          {{ $t('NoPermissionsToActivateCalibrationCertificateMsg') }}
        </template>
      </Tooltip>
      <v-row>
        <v-col>
          <VDivider />
        </v-col>
      </v-row>
      <template
        v-if="
          calibrationData?.newDownloadedCalibration && calibrationData?.existingCalibrations?.[0]
        "
      >
        <QaEquipmentActivateNewCalibrationDialog
          v-if="
            instanceOfUnicheckDeviceDto(qaEquipment) || instanceOfUnicheckSimulatorDto(qaEquipment)
          "
          :id="qaEquipment.id"
          v-model="isActivateCalibrationDialogOpen"
          :newCalibrationData="calibrationData.newDownloadedCalibration"
          :currentCalibrationData="calibrationData.existingCalibrations[0]"
          @click:contactSupport="
            toggleActivateCalibrationDialog(false);
            toggleContactSupportDialog(true);
          "
        />
      </template>
    </template>
    <QaEquipmentSupportRequestCalibrationInvalidDialog
      v-if="calibrationData?.existingCalibrations?.length"
      v-model="isContactSupportDialogOpen"
      :newCalibrationData="calibrationData.newDownloadedCalibration"
      :currentCalibrationData="calibrationData.existingCalibrations[0]"
      :lastReportedByDevice="calibrationData.lastReportedByDevice"
    />

    <!-- List of calibrations -->
    <template v-if="calibrationData?.existingCalibrations?.length">
      <v-row>
        <v-col cols="12">
          <h4>{{ $t('CalibrationInformation') }}</h4>
          <CalibrationInformation
            :calibrationData="calibrationData?.existingCalibrations?.[0]"
            colorizeState
            showSupportButton
            @click:contactSupport="toggleContactSupportDialog(true)"
          />
        </v-col>
      </v-row>
      <template v-if="calibrationData?.existingCalibrations?.length > 1">
        <v-row>
          <v-col>
            <VDivider />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h4>{{ $t('OutdatedCalibrations') }}</h4>
            <CalibrationInformation
              v-for="(calibration, index) in calibrationData?.existingCalibrations?.slice(1)"
              :key="`${calibration.calibrationCertificateNumber}-${index}`"
              :calibrationData="calibration"
            />
          </v-col>
        </v-row>
      </template>
    </template>
  </template>
</template>
